import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ConditionalRender } from '@ui';
import { EmployeeFormValues } from '@/types';
import { EmployeeRole } from '@/api/apis/resources';
import { Hub, TXCenter } from '@/api/apis/facilities';
import { OpoOrganization } from '@/api/apis/opo';
import { getShortTzByIanaName } from '@/utils/helpers';
import {
  ContractorRoleSpecificationSection,
  LocationInformationSection,
  UniformMeasurementsSection,
  UserDetailsSection,
} from './components/sections';
import { useContractorAffiliation } from './useContractorAffiliation';

type ContractorFormProps = {
  initialAirportName?: string;
  baseLocation?: Hub;
  txCenter?: TXCenter;
  opo?: OpoOrganization;
  isReadOnly?: boolean;
};

export const ContractorForm = ({
  initialAirportName,
  txCenter,
  opo,
  isReadOnly = false,
  baseLocation,
}: ContractorFormProps) => {
  const { control } = useFormContext<EmployeeFormValues>();
  const [roles, organType] = useWatch({ control, name: ['roles', 'organType'] });

  const { handleTxCenterSelect, handleOpoSelect, handleAffiliationTypeChange, affiliationIanaTimezone } =
    useContractorAffiliation({ txCenter, opo });

  const affiliationDisplayTimezone = useMemo(
    () => getShortTzByIanaName(affiliationIanaTimezone) || '',
    [affiliationIanaTimezone],
  );

  const isSpecialist = roles?.includes(EmployeeRole.ClinicalSpecialist);
  const isRetrievalSurgeon =
    roles?.includes(EmployeeRole.RetrievalSurgeon) || roles?.includes(EmployeeRole.ContractorRetrievalSurgeon);
  const showUniformMeasurements = !!(organType?.length && (isSpecialist || isRetrievalSurgeon));

  return (
    <>
      <UserDetailsSection isReadOnly={isReadOnly} isContractor />
      <ContractorRoleSpecificationSection
        isReadOnly={isReadOnly}
        txCenter={txCenter}
        opo={opo}
        onTxCenterSelect={handleTxCenterSelect}
        onOpoSelect={handleOpoSelect}
        onAffiliationTypeChange={handleAffiliationTypeChange}
      />
      <ConditionalRender show={showUniformMeasurements}>
        <UniformMeasurementsSection isReadOnly={isReadOnly} />
      </ConditionalRender>
      <LocationInformationSection
        isReadOnly={isReadOnly}
        baseLocation={baseLocation}
        isContractor
        initialAirportName={initialAirportName}
        affiliationDisplayTimezone={affiliationDisplayTimezone}
      />
    </>
  );
};
